<template>
  <div>
    <v-row class="demo-app-banner" no-gutters align="center" justify="center" v-if="$isReadOnly">
      <v-icon small class="mr-2">lock</v-icon>Indeego est en lecture seule. Vous pouvez consulter vos données et demander un export.
      <router-link v-if="canExport" to="/data-exports" class="ml-2" style="color:inherit;text-decoration:underline">Exporter mes données</router-link>
    </v-row>
    <v-row class="demo-app-banner" no-gutters align="center" justify="center" v-else-if="isDemoApp">
      <v-icon small class="mr-2">warning</v-icon>Ceci est un environnement de démonstration, ne pas entrer de données sensibles<v-icon small class="ml-2">warning</v-icon>
    </v-row>
    <div class="topbar">
      <v-row no-gutters align="center" justify="center" class="fill-height">
        <div>
          <v-row no-gutters align="center" justify="center" class="white--text font-weight-bold pl-4">
            <div class="text-uppercase primary--text">
              Administration Indeego
            </div>
          </v-row>
        </div>
        <v-spacer />
        <div class="mr-3">
          <user-menu />
        </div>
      </v-row>
    </div>
  </div>
</template>

<script>
import _ from 'lodash';
import { mapGetters, mapState } from 'vuex';
import UserMenu from '@/components/UserMenu.vue';

export default {
  name: `Topbar`,
  data: () => ({
    isSearchOpened: false,
    searchText: ``,
    searchSelectedInterventionIndex: 0,
    isSearching: false,
    searchTimeout: null,
  }),
  computed: {
    canExport() {
      if (!this.$store.state.platform.exportsVisible) return false;
      return this.me && this.company && this.me.role === `administrator` && this.me.Client_id === this.company.id && Boolean(this.company.slug);
    },
    ...mapState(`model/user`, [
      `me`,
      `isAggregatorUser`,
    ]),
    ...mapGetters(`model/user`, [
      `isIndeegoUser`,
    ]),
    ...mapState(`model/client`, [
      `company`,
      `rights`,
      `isAggregator`,
    ]),
    ...mapState(`ui/mainLayout`, {
      searchResults: `searchResultsInterventions`,
    }),
    ...mapState(`ui/mainLayout`, [`isDemoApp`]),
    canSeeStatistics() {
      return this.isAggregator && (this.isAggregatorUser || this.me.isIndeego) && this.me.clientAdmin;
    },
    canGoToAdmin() {
      return this.me && this.company && ((this.me.clientAdmin && this.me.Client_id === this.company.id) || this.me.isIndeego);
    },
    canCreateIntervention() {
      let can = Boolean(this.me.clientAdmin);

      if (!can) {
        (this.me.rights || []).forEach(userRight => {
          const canOnMaker = userRight.right.id === 3;
          can = can || canOnMaker;
        });
      }

      return can;
    },
    isSearchTextEmpty() {
      return this.searchText === `` || this.searchText === null;
    },
    hasNoResults() {
      return !this.isSearchTextEmpty && !this.isSearching && !this.searchResults.length;
    },
    searchSelectedIntervention() {
      return this.searchResults[this.searchSelectedInterventionIndex];
    },
    companyInitials() {
      const splittedName = this.company.name.split(` `);
      if (splittedName.length > 1) {
        return `${splittedName.map(word => word.substring(0, 1)).join(`.`)}.`;
      }

      const name = this.company.name.toUpperCase();
      if (name.length <= 8) {
        return name;
      }

      return name.substring(0, 3);
    },
    companyLogo() {
      try {
        if (this.company.logoUrl.startsWith(`/`)) {
          return require(`@/assets/slug/${this.company.slug}.png`); // eslint-disable-line
        }
        return this.company.logoUrl;
      } catch (e) {
        return null;
      }
    },
  },
  methods: {
    async performSearch() {
      this.isSearching = true;
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(async () => {
        await this.$store.dispatch(`ui/mainLayout/searchInterventions`, this.searchText);
        this.isSearching = false;
      }, 600);
    },
    goToIntervention(intervention) {
      if (this.isSearchTextEmpty || this.hasNoResults) {
        return;
      }
      this.$router.push({
        name: `InterventionsIntervention`,
        params: {
          interventionId: intervention.id,
        },
      });
      this.isSearchOpened = false;
    },
    setSelectedSearchIntervention(intervention) {
      this.searchSelectedInterventionIndex = _.findIndex(this.searchResults, { id: intervention.id });
    },
    selectPreviousSearchIntervention() {
      if (this.searchSelectedInterventionIndex > 0) {
        this.searchSelectedInterventionIndex -= 1;
      }
    },
    selectNextSearchIntervention() {
      if (this.searchSelectedInterventionIndex < (this.searchResults.length - 1)) {
        this.searchSelectedInterventionIndex += 1;
      }
    },
    goToSearchSelectedInterventionIndex() {
      return this.goToIntervention(this.searchSelectedIntervention);
    },
    async logout() {
      await this.$store.dispatch(`model/auth/logout`);
      this.$router.push({ name: `login` });
    },
  },
  mounted() {
    if (this.isDemoApp) {
      window.document.title = `Démo Indeego`;
    }
  },
  watch: {
    searchText(searchText) {
      if (_.trim(searchText).length) {
        this.performSearch();
      }
    },
  },
  components: {
    UserMenu,
  },
};
</script>

<style lang="scss" scoped>
  $topbar-height: 65px;
  $logo-margin: 9px;
  $logo-max-height: $topbar-height - 2*$logo-margin;
  .topbar {
    position: relative;
    z-index: 100;
    &, * {
      font-size: 12.6px;
    }
    background: #FFF;
    -webkit-box-shadow: 0px 0px 10px 0px rgba(50, 50, 50, 0.5);
    -moz-box-shadow:    0px 0px 10px 0px rgba(50, 50, 50, 0.5);
    box-shadow:         0px 0px 10px 0px rgba(50, 50, 50, 0.5);

    height: $topbar-height;
  }

  .demo-app-banner {
    position: relative;
    background-color: #ce0000;
    height: 30px;
    font-weight: 500;
    font-size: 0.9rem;
    color: #FFF;
    z-index: 100;
    .v-icon {
      color: inherit;
    }
  }

  .client-logo {
    height: $logo-max-height;
    margin: $logo-margin;
    padding: 2px;
    background-color: #FFF;
    border-radius: 5px;

    img {
      max-height: 100%;
      max-width: 100px;
    }
  }

  .nav-link {
    border-radius: 5px;
    border: 1px solid transparent;
    background-color: transparent;
    color: $primaryColor;
    font-weight: 700;
    padding: 6px 15px;
    margin: 0px 5px;
    text-decoration: none !important;
    border: 3px solid transparent;

    &.router-link-active, &:hover {
      background: #FFF;
      -webkit-box-shadow: 2px 2px 8px 2px rgba(19, 26, 154, 0.16);
      -moz-box-shadow:    2px 2px 8px 2px rgba(19, 26, 154, 0.16);
      box-shadow:         2px 2px 8px 2px rgba(19, 26, 154, 0.16);
      border: 3px solid $secondaryColor;
    }
  }

  .nav-icon {
    color: #FFF !important;
    font-size: 1.2rem !important;
    font-weight: 500;
    padding: 5px !important;
    background-color: $primaryColor;
    border-radius: 100%;
    .v-btn & {
      height: auto !important;
      width: auto !important;
    }
  }

  .secondary-nav-link {
    text-decoration: none !important;
    color: #FFF !important;
    font-weight: 500;

    &:hover {
      background-color: saturate($primaryColor, 40);
      -webkit-box-shadow: 2px 2px 12px 3px rgba(19, 26, 154, 0.2);
      -moz-box-shadow:    2px 2px 12px 3px rgba(19, 26, 154, 0.2);
      box-shadow:         2px 2px 12px 3px rgba(19, 26, 154, 0.2);
    }

    &.highlighted {
      padding: 5px !important;
      padding-top: 7px !important;
      padding-bottom: 9px !important;
      padding-right: 10px !important;
      background-color: $primaryColor;
      border-radius: 20px;

      .v-icon {
        color: #FFF;
        font-size: 1.2rem;
        padding: 3px;
      }
    }

    &.v-btn {
      height: auto !important;
      width: auto !important;
      min-width: 0px !important;
      padding: 0 !important;
      text-transform: none;
      letter-spacing: inherit;
    }
  }

  .dropdown-icon {
    font-size: 0.9rem !important;
    color: inherit !important;
    opacity: .7;
  }

  .search-card {
    width: 550px;
  }

  .search-card-header {
    padding: 1rem;
  }

  .search-card-header-cancel {
    text-decoration: none;
  }

  .search-no-text-or-no-results {
    color: #777;
    font-size: 1.3rem;
    text-align: center;
    line-height: 2.4rem;
    margin: 1.4rem 2.4rem 1.4rem;

    .v-icon {
      font-size: 2.5rem;
    }
  }

  .search-card-results {
    padding: 0 1rem 1rem;

    .search-result-item {
      &:hover {
        cursor: pointer;
      }

      &.hovered {
        background-color: #EEE;
      }

      &:not(:last-of-type) {
        border-bottom: 1px solid #CCC;
      }
    }
  }

</style>
